"use client";

import { FC, ReactNode } from "react";
import { FileText } from "lucide-react";
import { useRouter } from "next/navigation";
import { formatTimeAgo } from "@/services-frontend/notificationService/notificationServices";
import { useLiveBlock } from "@/indexedDB/hooks/useLiveBlock";
import { useWorkspaceContext } from "@/contexts/workspaceContext";
import { AvatarImage } from "@/components/tailwind/ui/memberAvatar";
import { MentionHoverCard } from "@/components/tailwind/mention-hover-card";
import { renderIcon } from "@/components/tailwind/editor/renderIcon";
import { buildNoteHref } from "@/utils/noteUrl";

export const NotificationAvatar: FC<{
  name?: string;
  userId?: string;
  email?: string;
}> = ({ name, userId, email }) => {
  const router = useRouter();
  const { getMemberImage, getMemberId } = useWorkspaceContext();
  const image = getMemberImage(userId) || getMemberImage(email);
  const letter = (name?.charAt(0) || "U").toUpperCase();
  // The actor's userId may come through directly or need resolving from email.
  const resolvedId = userId || getMemberId(email);

  const avatar = (
    <AvatarImage
      src={image}
      alt={name || ""}
      className="h-6 w-6 rounded-full border border-gray-200 object-cover dark:border-gray-600"
      fallback={
        <div className="flex h-6 w-6 items-center justify-center rounded-full border border-gray-300 bg-gray-50 text-[11px] font-semibold text-gray-600 dark:border-gray-600 dark:bg-[rgb(42,42,42)] dark:text-gray-300">
          {letter}
        </div>
      }
    />
  );

  // No resolvable user (e.g. a system notification) → plain avatar.
  if (!resolvedId) return avatar;

  // Hover shows the shared profile card; clicking opens the actor's profile.
  return (
    <MentionHoverCard userId={resolvedId} userName={name || ""} userEmail={email}>
      <span
        className="cursor-pointer"
        onClick={(e) => {
          e.stopPropagation();
          router.push(`/userprofile/${resolvedId}`);
        }}
      >
        {avatar}
      </span>
    </MentionHoverCard>
  );
};

/** Inline page-reference chip: live title + icon from IndexedDB, falling back
 *  to the notification's title. Links to the note. */
export const NotificationPageLink: FC<{ noteId?: string; title?: string }> = ({
  noteId,
  title,
}) => {
  const block = useLiveBlock(noteId || null);
  const blockValue = block?.value as { title?: string; icon?: string } | undefined;

  const label = blockValue?.title?.trim() || title || "New Page";
  const icon = blockValue?.icon?.trim() || "";

  // Same renderer as the sidebar and breadcrumbs, so an Iconify id
  // ("tabler:alert-circle-filled") draws its glyph here too instead of leaking
  // the raw string into the sentence. `rounded-sm` is inherited by uploaded
  // image icons, which render with borderRadius: "inherit".
  const iconNode = icon ? (
    <span className="mr-1 inline-flex h-3.5 w-3.5 items-center justify-center rounded-sm align-[-0.2em] leading-none">
      {renderIcon(icon, { style: { width: "100%", height: "100%", fontSize: 13 } })}
    </span>
  ) : (
    <FileText className="mr-1 inline-block h-3.5 w-3.5 align-[-0.2em] text-gray-400 dark:text-gray-500" />
  );

  // Inline (not inline-flex) so the title shares the sentence's baseline.
  const inner = (
    <>
      {iconNode}
      {label}
    </>
  );

  const base =
    "rounded px-1 -mx-0.5 font-medium text-gray-700 dark:text-gray-200";

  if (!noteId) {
    return <span className={base}>{inner}</span>;
  }

  return (
    <a
      href={buildNoteHref(noteId, blockValue?.title || title, block?.blockType)}
      className={`${base} transition-colors hover-component `}
    >
      {inner}
    </a>
  );
};

/** Inline workspace-reference chip. Clickable only when the workspace is one
 *  the user currently belongs to — clicking switches to it and opens its home.
 *  Falls back to plain text otherwise (e.g. an old invite the user has left). */
export const NotificationWorkspaceLink: FC<{
  workspaceId?: string;
  workspaceName?: string;
}> = ({ workspaceId, workspaceName }) => {
  const router = useRouter();
  const { workspaces, currentWorkspace, switchWorkspace } = useWorkspaceContext();

  const label = workspaceName?.trim() || "a workspace";
  const base = "font-medium text-gray-700 dark:text-gray-300";

  // Only a workspace the user is a member of is resolvable here — and only a
  // resolvable one can be switched into.
  const target = workspaceId ? workspaces.find((ws) => ws._id === workspaceId) : undefined;

  if (!target) return <span className={base}>{label}</span>;

  const openWorkspaceHome = () => {
    // Passing the destination makes the notes-layout redirect land on home too,
    // so the switch and this navigation agree instead of competing.
    if (currentWorkspace?._id !== target._id) switchWorkspace(target, "/notes/home");
    router.push("/notes/home");
  };

  return (
    <span
      onClick={(e) => {
        e.stopPropagation();
        openWorkspaceHome();
      }}
      className={`${base} cursor-pointer rounded px-1 -mx-0.5 transition-colors hover-component`}
    >
      {label}
    </span>
  );
};

interface NotificationItemProps {
  /** 24px avatar or type icon shown on the left. */
  icon: ReactNode;
  /** Top line — the actor's name (or a status heading), shown in bold. */
  title: ReactNode;
  /** Second line — the action message shown beneath the name. */
  message?: ReactNode;
  createdAt?: string;
  unread?: boolean;
  /** Optional inline controls such as Approve / Deny buttons. */
  actions?: ReactNode;
}

const NotificationItem: FC<NotificationItemProps> = ({
  icon,
  title,
  message,
  createdAt,
  unread = true,
  actions,
}) => {
  return (
    <div className="flex gap-3 px-2 py-2">
      <div className="mt-0.5 flex-shrink-0">{icon}</div>

      <div className="min-w-0 flex-1">
        {/* Top line: name + timestamp / unread dot */}
        <div className="flex items-start justify-between gap-3">
          <p className="min-w-0 flex-1 truncate text-sm font-semibold text-gray-700 dark:text-gray-200">
            {title}
          </p>
          <div className="mt-px flex flex-shrink-0 items-center gap-1.5 whitespace-nowrap text-xs text-gray-400 dark:text-gray-500">
            {createdAt ? <span>{formatTimeAgo(createdAt)}</span> : null}
            {unread && (
              <span
                aria-label="Unread"
                title="Unread"
                className="h-2 w-2 rounded-full bg-blue-500"
              />
            )}
          </div>
        </div>

        {/* Second line: the message */}
        {message ? (
          <p className="mt-0.5 text-sm leading-snug text-gray-500 dark:text-gray-400">
            {message}
          </p>
        ) : null}

        {actions ? <div className="mt-2.5 flex items-center gap-2">{actions}</div> : null}
      </div>
    </div>
  );
};

export default NotificationItem;
